import Utility from '@/common/UtilityFunctions.js';
import type {
  PaymentIntervalDto,
  Proposal,
  ProposalClause,
  ProposalCover,
  ProposalDocument
} from '@/interfaces/proposal';
import type {
  BasketProposal,
  BasketProposalClause,
  BasketProposalCover,
  BasketProposalDocument,
  BasketProposalDownload,
  BasketProposalPaymentInterval
} from '@/store/modules/interfaces/ProposalBasketStoreState';

export const formatPrice = (price: string | number): string => {
  if (typeof price === 'number') {
    return Utility.formatNumberInDKLocale(price);
  }
  return price;
};

export const mapPaymentInterval = (
  interval: PaymentIntervalDto
): BasketProposalPaymentInterval => ({
  id: interval.id,
  description: interval.description
});

export const mapProposalCovers = (cover: ProposalCover): BasketProposalCover => ({
  name: cover.name,
  hasTooltip: cover.hasTooltip,
  tooltip: cover.tooltip,
  value: cover.value,
  renderingType: cover.renderingType,
  amountType: cover.amountType,
  groupedCovers: cover.groupedCovers ? cover.groupedCovers.map(mapProposalCovers) : undefined
});

export const mapProposalClause = (clause: ProposalClause): BasketProposalClause => ({
  id: clause.id,
  title: clause.title,
  description: clause.description
});

export const mapToBasketDownloads = (proposal: Proposal): BasketProposalDownload[] => {
  const downloads: BasketProposalDownload[] = [];
  proposal.pidList.forEach((pid) =>
    downloads.push({
      text: pid.pidText,
      url: pid.pidUrl
    })
  );

  proposal.tacList.forEach((tac) =>
    downloads.push({
      text: tac.portalText,
      url: tac.mediaItemUrl
    })
  );
  return downloads;
};

export const mapDocument = (proposal: ProposalDocument): BasketProposalDocument => ({
  link: proposal.link,
  title: proposal.title
});

export const mapToBasketProposalModel = (proposal: Proposal): BasketProposal => ({
  id: proposal.id,
  name: proposal.name,
  type: proposal.type,
  productType: proposal.productType,
  details: proposal.asset.assetDetailSingleLine,
  isDigitallyCreated: proposal.isDigitallyCreated,
  externalProposalNumber: proposal.externalProposalNumber,
  fees: proposal.feesAsNumber,
  price: proposal.priceAsNumber,
  covers: proposal.covers.map(mapProposalCovers),
  deductible: proposal.deductible,
  downloads: mapToBasketDownloads(proposal),
  clauses: proposal.clauses.map(mapProposalClause),
  document: proposal.document ? mapDocument(proposal.document) : undefined,
  paymentInformation: {
    selectedPaymentInterval: mapPaymentInterval(
      proposal.paymentInformation.selectedPaymentInterval
    ),
    paymentIntervals:
      proposal.paymentInformation.paymentMethods[0].paymentIntervals.map(mapPaymentInterval)
  },
  productInformation: proposal.productInformationBoxText,
  showYearZeroText: proposal.showYearZeroText
});
